import { ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { css, type StyleDeclarationValue, StyleSheet } from 'aphrodite';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import * as Api from '@ViewModels';
import { TestingContactsDrawerContent } from './testing/TestingContactsDrawerContent';
import type { SegmentFilterMode } from './testing/types';
import { useCanvasState } from './hooks/useCanvasState';
import { useCanvasSync } from './hooks/useCanvasSync';
import { useStepMutations } from './hooks/useStepMutations';
import { Button } from '../../../web/components-v2/Button';
import { TabToggle } from '../../../web/components-v2/TabToggle';
import {
	AppDrawerToolbarButton,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../web/components-v2/Drawer/AppDrawer';
import { AutomationFlowCanvas } from '../../../web/containers/AutomationFlows/components/AutomationFlowCanvas';
import { AutomationFlowErrorBoundary } from '../../../web/containers/AutomationFlows/components/AutomationFlowErrorBoundary';
import { DiscardDialog } from '../../../web/containers/AutomationFlows/components/DiscardDialog';
import { DeleteAutomationDialog } from '../../../web/containers/AutomationFlows/components/DeleteAutomationDialog';
import { PublishDialog } from '../../../web/containers/AutomationFlows/components/PublishDialog';
import { RefreshTimingBanner } from '../../../web/containers/AutomationFlows/components/RefreshTimingBanner';
import { NodePalette } from '../../../web/containers/AutomationFlows/components/NodePalette';
import { useAutomationFlow } from '../../../web/containers/AutomationFlows/hooks/useAutomationFlow';
import { useAutoSave } from '../../../web/containers/AutomationFlows/hooks/useAutoSave';
import { useAutomationStatus } from '../../../web/containers/AutomationFlows/hooks/useAutomationStatus';
import { useCanvasMode } from '../../../web/containers/AutomationFlows/hooks/useCanvasMode';
import { useDiscardDraft } from '../../../web/containers/AutomationFlows/hooks/useDiscardDraft';
import { useDraftVersionState } from '../../../web/containers/AutomationFlows/hooks/useDraftVersionState';
import { usePublishAutomation } from '../../../web/containers/AutomationFlows/hooks/usePublishAutomation';
import {
	useAutomationTemplateHasDraftQuery,
	useAutomationTemplateSupportedStepsQuery,
} from '../../../queries/Automation';
import { useUnsavedChanges } from '../../../web/containers/AutomationFlows/hooks/useUnsavedChanges';
import type { ValidationError } from '../../../web/containers/AutomationFlows/utils/validateAutomation';
import {
	NODE_TYPES,
	type CanvasMode,
	type SegmentNodeData,
	type TriggerNodeData,
} from '../../../web/containers/AutomationFlows/types';
import { isPolicyBasedSegmentTrigger } from '../../../web/containers/AutomationFlows/utils/segmentCriteria';
import { DefaultUniversalAssistantChat } from '../../../web/containers/templates/AssistantChat';
import { EditorActionBar } from '../../../web/pages/AutomationFlowEditor/components/EditorActionBar';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { AutomationTitleEditor } from './AutomationTitleEditor';
import { AutomationCanvasEditorStepsProvider } from './AutomationCanvasEditorStepsProvider';
import { white, borderOutline, brandPrimaryDark, brandSecondary, mention } from '../../../web/styles/colors';

// ---------------------------------------------------------------------------
// Styles shared between web and admin canvas editors
// ---------------------------------------------------------------------------

export const canvasEditorStyles = StyleSheet.create({
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		paddingTop: 20,
		paddingBottom: 20,
		minHeight: 0,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
	},
	headerLeft: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: 0,
		flex: 1,
	},
	canvasContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: white,
		borderRadius: 12,
		border: `1px solid ${borderOutline}`,
		boxShadow: '0 20px 50px rgba(15, 23, 42, 0.06)',
		overflow: 'hidden',
		minHeight: 650,
		// Some pages rely on % heights; make this explicit so the canvas has a definite size.
		height: '100%',
	},
	publishedPreviewBanner: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
		padding: '10px 16px',
		backgroundColor: mention,
		color: brandPrimaryDark,
		borderRadius: 8,
		fontSize: 13,
		fontWeight: 500,
		border: `1px solid ${brandSecondary}`,
	},
	nodePaletteContainer: {
		padding: 16,
		height: '100%',
		overflowY: 'auto',
	},
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Render props provided to the layout function. */
export interface AutomationCanvasEditorRenderProps {
	/** The full content area: header, toggle, action bar, preview banner, and canvas. */
	contentArea: ReactNode;
	/** Drawer panels (StickyAppDrawerContent elements). */
	drawerPanels: ReactNode;
	/** Drawer toolbar buttons (AppDrawerToolbarButton elements). */
	drawerToolbarButtons: ReactNode;
}

export interface AutomationCanvasEditorActionBarRenderProps {
	hasDraft: boolean;
	hasPublished: boolean;
	useDraft: boolean;
	isPublishedPreview: boolean;
	isReadOnly: boolean;
	canPublish: boolean;
	canDiscard: boolean;
	isPublishing: boolean;
	isDiscarding: boolean;
	isActive: boolean;
	onPublish: () => void;
	onDiscard: () => void;
	onToggleStatus: () => void;
	onDelete: () => void;
}

export interface AutomationCanvasEditorProps {
	templateId: string;
	template: Api.IAutomationTemplate | undefined;
	isLoading: boolean;
	isError: boolean;
	error: { message?: string } | null;
	refetch: () => void;
	impersonationContext?: Api.IImpersonationContext;
	onPublishSuccess: () => void;
	onDeleteSuccess: () => void;
	/** Additional Aphrodite styles merged onto the content wrapper div. */
	contentStyles?: StyleDeclarationValue[];
	/** When true, disable inline title editing and all canvas mutations in the UI. */
	isReadOnly?: boolean;
	/** Optional custom action bar renderer. When provided, replaces the default action bar. */
	renderActionBar?: (props: AutomationCanvasEditorActionBarRenderProps) => ReactNode;
	/** Render a loading state with the given message. */
	renderLoading: (message: string) => ReactNode;
	/** Render an error state with the given message and retry callback. */
	renderError: (errorMessage: string, onRetry: () => void) => ReactNode;
	/** When false, hides the testing contacts drawer panel and toolbar button. Defaults to true. */
	showTestingContacts?: boolean;
	/** Layout render prop — receives content area and drawer pieces. */
	children: (renderProps: AutomationCanvasEditorRenderProps) => ReactNode;
}

function collectCriteriaProperties(
	criteria: Api.IContactFilterCriteria | undefined,
	properties: Set<Api.ContactFilterCriteriaProperty>
) {
	if (!criteria) {
		return;
	}

	if (criteria.property !== undefined && criteria.property !== null) {
		properties.add(criteria.property);
	}

	if (Array.isArray(criteria.criteria)) {
		for (const childCriteria of criteria.criteria) {
			collectCriteriaProperties(childCriteria, properties);
		}
	}
}

function detectSegmentFilterMode(nodes: { type?: string; data?: unknown }[]): SegmentFilterMode {
	const criteriaProperties = new Set<Api.ContactFilterCriteriaProperty>();
	let hasPolicyBasedTrigger = false;

	for (const node of nodes) {
		if (node.type === NODE_TYPES.SEGMENT) {
			const segmentData = node.data as SegmentNodeData;
			collectCriteriaProperties(segmentData.primaryCriteria, criteriaProperties);
			for (const criteria of segmentData.additionalCriteria || []) {
				collectCriteriaProperties(criteria, criteriaProperties);
			}
		}

		if (node.type === NODE_TYPES.TRIGGER) {
			const triggerData = node.data as TriggerNodeData;
			hasPolicyBasedTrigger = hasPolicyBasedTrigger || isPolicyBasedSegmentTrigger(triggerData.automationTrigger);
		}
	}

	const hasTagCriteria = criteriaProperties.has(Api.ContactFilterCriteriaProperty.Tag);
	const hasPolicyCriteria = criteriaProperties.has(Api.ContactFilterCriteriaProperty.Policy) || hasPolicyBasedTrigger;

	if (hasTagCriteria && hasPolicyCriteria) {
		return 'mixed';
	}
	if (hasPolicyCriteria) {
		return 'policy';
	}
	if (hasTagCriteria) {
		return 'tag';
	}
	return 'unknown';
}

const STEP_TYPE_TO_NODE_TYPE: Partial<Record<Api.AutomationStepType, string>> = {
	[Api.AutomationStepType.Email]: NODE_TYPES.EMAIL_STEP,
	[Api.AutomationStepType.Texting]: NODE_TYPES.TEXTING_STEP,
	[Api.AutomationStepType.HtmlNewsletter]: NODE_TYPES.HTML_NEWSLETTER_STEP,
	[Api.AutomationStepType.HandwrittenCard]: NODE_TYPES.HANDWRITTEN_CARD_STEP,
	[Api.AutomationStepType.ActionItem]: NODE_TYPES.ACTION_ITEM_STEP,
	[Api.AutomationStepType.Opportunity]: NODE_TYPES.OPPORTUNITY_STEP,
	[Api.AutomationStepType.AddTag]: NODE_TYPES.ADD_TAG_STEP,
	[Api.AutomationStepType.RemoveTag]: NODE_TYPES.REMOVE_TAG_STEP,
	[Api.AutomationStepType.NoAction]: NODE_TYPES.NO_ACTION_STEP,
	[Api.AutomationStepType.Switch]: NODE_TYPES.SWITCH_STEP,
};

const API_BACKED_PALETTE_NODE_TYPES = Object.values(STEP_TYPE_TO_NODE_TYPE).filter(
	(nodeType): nodeType is string => !!nodeType
);

function getSupportedNodeTypes(stepModelTypes: Api.AutomationStepModelType[]) {
	const supportedNodeTypes = new Set<string>();

	stepModelTypes.forEach(stepModelType => {
		const stepType = Api.apiModelTypeToAutomationStepType(stepModelType as Api.ApiModelType);
		const nodeType = stepType ? STEP_TYPE_TO_NODE_TYPE[stepType] : undefined;
		if (nodeType) {
			supportedNodeTypes.add(nodeType);
		}
	});

	return supportedNodeTypes;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AutomationCanvasEditor({
	templateId,
	template,
	isLoading,
	isError,
	error,
	refetch,
	impersonationContext,
	onPublishSuccess,
	onDeleteSuccess,
	contentStyles = [],
	isReadOnly = false,
	showTestingContacts = true,
	renderActionBar,
	renderLoading,
	renderError,
	children,
}: AutomationCanvasEditorProps) {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	const accountFeatures = impersonationContext?.account
		? impersonationContext.account.features
		: userSession?.account?.features;
	const textingEnabled = accountFeatures?.texting?.enabled === true;
	const htmlNewslettersEnabled = accountFeatures?.htmlNewsletter?.enabled === true;
	const handwrittenCardsEnabled = accountFeatures?.handwrittenCards?.enabled === true;
	const opportunitiesEnabled = accountFeatures?.boards?.opportunitiesEnabled === true;

	// -- Draft / Published state --
	const { data: hasAnyDraftInTree = false } = useAutomationTemplateHasDraftQuery({
		template,
		enabled: !!templateId && !!template,
		impersonationContext,
	});
	const { useDraft, hasDraft, hasPublished, handleToggleDraft } = useDraftVersionState(template, {
		hasDraftOverride: hasAnyDraftInTree,
	});
	const isPublishedPreview = hasPublished && hasDraft && !useDraft;
	const canvasMode: CanvasMode = isReadOnly || isPublishedPreview ? 'preview' : 'editing';
	const canvasModeConfig = useCanvasMode(canvasMode);
	const supportedStepsQuery = useAutomationTemplateSupportedStepsQuery({
		id: templateId,
		// The supported-steps result is only consumed via the step palette / drag-drop gating.
		// Skip the fetch entirely in read-only and published-preview modes where the palette is hidden.
		enabled: !!templateId && canvasModeConfig.showNodePalette,
		impersonationContext,
	});

	// -- Local canvas state --
	const canvasState = useCanvasState();
	const {
		nodes,
		edges,
		testContacts,
		isInitialized,
		canvasSyncVersion,
		excludedStepTypes,
		nodesRef,
		edgesRef,
		selectedSwitchNodeId,
		setCanvasSyncVersion,
		handleNodesChange,
		handleEdgesChange,
		handleTestContactsChange,
		handleSelectionChange,
	} = canvasState;

	const effectiveExcludedStepTypes = useMemo(() => {
		const stepTypes = new Set(excludedStepTypes);

		if (!textingEnabled) {
			stepTypes.add(NODE_TYPES.TEXTING_STEP);
		}
		if (!htmlNewslettersEnabled) {
			stepTypes.add(NODE_TYPES.HTML_NEWSLETTER_STEP);
		}
		if (!handwrittenCardsEnabled) {
			stepTypes.add(NODE_TYPES.HANDWRITTEN_CARD_STEP);
		}
		if (!opportunitiesEnabled) {
			stepTypes.add(NODE_TYPES.OPPORTUNITY_STEP);
		}

		// Use `isSuccess` (not `!isLoading`) so we only apply API gating once we have
		// authoritative data. An empty array is a valid, meaningful response — every
		// API-backed palette type should be excluded. Skipping the block on `[]` would
		// silently show gated steps. Conversely, on error `data` is undefined and we
		// intentionally leave the API-backed steps un-gated rather than hide them all.
		const supportedStepModelTypes = supportedStepsQuery.data;
		if (supportedStepsQuery.isSuccess && Array.isArray(supportedStepModelTypes)) {
			const supportedNodeTypes = getSupportedNodeTypes(supportedStepModelTypes);
			API_BACKED_PALETTE_NODE_TYPES.forEach(nodeType => {
				if (!supportedNodeTypes.has(nodeType)) {
					stepTypes.add(nodeType);
				}
			});
		}

		return Array.from(stepTypes);
	}, [
		excludedStepTypes,
		handwrittenCardsEnabled,
		htmlNewslettersEnabled,
		opportunitiesEnabled,
		supportedStepsQuery.data,
		supportedStepsQuery.isSuccess,
		textingEnabled,
	]);

	// -- Flow expansion (current view: draft or published) --
	const {
		nodes: expandedNodes,
		edges: expandedEdges,
		isExpanding,
		expandedVersion,
	} = useAutomationFlow({
		template: template ?? null,
		useDraft,
		impersonationContext,
		cacheBuster: canvasSyncVersion,
	});

	// -- Flow expansion (draft, for publish validation) --
	// Publishing always commits the DRAFT version, even if the user is currently previewing the published flow.
	const {
		nodes: draftExpandedNodes,
		edges: draftExpandedEdges,
		isExpanding: isDraftExpanding,
		expandedVersion: draftExpandedVersion,
	} = useAutomationFlow({
		template: template ?? null,
		useDraft: true,
		impersonationContext,
		cacheBuster: canvasSyncVersion,
	});

	const flowForPublishValidation = useMemo(() => {
		if (!hasDraft) {
			return undefined;
		}

		// Publishing always commits the DRAFT version, even if the user is currently previewing the published flow.
		// When already editing the draft, prefer the live canvas graph so validation messages reflect
		// unsaved local segment/case naming edits immediately.
		if (useDraft) {
			return {
				nodes,
				edges,
				isReady: expandedVersion === 'draft' && !isExpanding && isInitialized,
			};
		}

		return {
			nodes: draftExpandedNodes,
			edges: draftExpandedEdges,
			isReady: draftExpandedVersion === 'draft' && !isDraftExpanding,
		};
	}, [
		hasDraft,
		useDraft,
		nodes,
		edges,
		expandedVersion,
		isExpanding,
		isInitialized,
		draftExpandedNodes,
		draftExpandedEdges,
		draftExpandedVersion,
		isDraftExpanding,
	]);

	const segmentFilterMode = useMemo(() => {
		const sourceNodes = expandedNodes.length > 0 ? expandedNodes : nodes;
		return detectSegmentFilterMode(sourceNodes);
	}, [expandedNodes, nodes]);

	// -- Auto-save --
	const autoSave = useAutoSave({
		templateId,
		enabled: Boolean(templateId) && !isPublishedPreview && !isReadOnly,
		impersonationContext,
	});
	const [highlightedValidationIssue, setHighlightedValidationIssue] = useState<ValidationError | null>(null);

	// -- Publish --
	const publishAutomation = usePublishAutomation({
		template,
		impersonationContext,
		flow: flowForPublishValidation,
		onPublishSuccess,
		onPublishError: publishError => {
			toast.error(publishError?.systemMessage || 'Failed to publish automation');
		},
	});

	useEffect(() => {
		if (publishAutomation.validationResult?.isValid) {
			setHighlightedValidationIssue(null);
		}
	}, [publishAutomation.validationResult]);

	// -- Discard draft --
	const discardDraft = useDiscardDraft({
		templateId,
		template,
		hasDraftOverride: hasDraft,
		impersonationContext,
		onDiscardSuccess: () => {
			autoSave.markClean();
			// Discarding removes the draft version on the server; immediately switch the UI back to published
			// so the canvas can re-sync without waiting for a full refetch cycle.
			handleToggleDraft(false);
			refetch();
		},
		onDiscardError: discardError => {
			toast.error(discardError?.systemMessage || 'Failed to discard draft');
		},
	});

	// -- Automation status (active / paused) --
	const automationStatus = useAutomationStatus({
		templateId,
		currentStatus: template?.status,
		impersonationContext,
	});

	// -- Sync effects (version toggle, contact enrichment, structural changes) --
	const { activeContacts } = useCanvasSync({
		expandedNodes,
		expandedEdges,
		isExpanding,
		expandedVersion,
		useDraft,
		isInitialized,
		testContacts,
		nodesRef,
		setNodes: canvasState.setNodes,
		setEdges: canvasState.setEdges,
		setCanvasSyncVersion,
		setIsInitialized: canvasState.setIsInitialized,
	});

	// -- Mutations & action handlers --
	const mutations = useStepMutations({
		templateId,
		template,
		impersonationContext,
		nodesRef,
		edgesRef,
		refetch,
		onDeleteSuccess,
		setCanvasSyncVersion,
	});

	// -- Unsaved changes warning --
	useUnsavedChanges({ isDirty: autoSave.isDirty });

	// -- Publish / Discard / Delete handlers --
	const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const handlePublishClick = useCallback(() => {
		// We need the expanded draft graph for validations like "segment must contain at least one step".
		if (flowForPublishValidation && !flowForPublishValidation.isReady) {
			toast.info('Preparing automation for publishing...');
			return;
		}

		const result = publishAutomation.validate();
		setHighlightedValidationIssue(result.errors[0] || null);
		setIsPublishDialogOpen(true);
	}, [flowForPublishValidation, publishAutomation]);

	const handlePublishConfirm = useCallback(async () => {
		try {
			await publishAutomation.publish();
			setHighlightedValidationIssue(null);
			setIsPublishDialogOpen(false);
		} catch {
			// Error already handled by onPublishError callback
		}
	}, [publishAutomation]);

	const handleClosePublishDialog = useCallback(() => {
		setIsPublishDialogOpen(false);
	}, []);

	const handleValidationIssueSelect = useCallback((issue: ValidationError) => {
		setHighlightedValidationIssue(issue);
		setIsPublishDialogOpen(false);
	}, []);

	const handleDiscardClick = useCallback(() => {
		setIsDiscardDialogOpen(true);
	}, []);

	const handleDiscardConfirm = useCallback(async () => {
		try {
			await discardDraft.discard();
			toast.success('Draft changes discarded');
			setIsDiscardDialogOpen(false);
		} catch {
			// Error already handled by onDiscardError callback
		}
	}, [discardDraft]);

	const handleDeleteClick = useCallback(() => {
		setIsDeleteDialogOpen(true);
	}, []);

	const handleDeleteConfirm = useCallback(async () => {
		try {
			await mutations.handleDelete();
			setIsDeleteDialogOpen(false);
		} catch {
			// Error already handled by onDeleteError callback
		}
	}, [mutations]);

	// -----------------------------------------------------------------------
	// Loading / error gates
	// -----------------------------------------------------------------------

	if (templateId && (isLoading || isExpanding) && !isInitialized) {
		return <>{renderLoading(isLoading ? 'Loading automation...' : 'Expanding flow...')}</>;
	}

	if (templateId && (isError || !template)) {
		return <>{renderError(error?.message || 'An unknown error occurred', () => refetch())}</>;
	}

	// -----------------------------------------------------------------------
	// Render pieces
	// -----------------------------------------------------------------------

	const contentArea = (
		<div className={css(canvasEditorStyles.content, ...contentStyles)}>
			<div className={css(canvasEditorStyles.header)}>
				<div className={css(canvasEditorStyles.headerLeft)}>
					<AutomationTitleEditor
						template={template}
						impersonationContext={impersonationContext}
						isPublishedPreview={isPublishedPreview}
						readOnly={isReadOnly}
						defaultName='Automation Flow Builder'
					/>
				</div>
				{(hasDraft || hasPublished) && (
					<TabToggle
						items={[
							{ value: 'published', label: 'Published', disabled: !hasPublished },
							{ value: 'draft', label: 'Draft', disabled: !hasDraft },
						]}
						value={useDraft ? 'draft' : 'published'}
						onValueChange={v => handleToggleDraft(v === 'draft')}
					/>
				)}
			</div>
			{renderActionBar ? (
				renderActionBar({
					hasDraft,
					hasPublished,
					useDraft,
					isPublishedPreview,
					isReadOnly,
					canPublish: publishAutomation.canPublish,
					canDiscard: discardDraft.canDiscard,
					isPublishing: publishAutomation.isPublishing,
					isDiscarding: discardDraft.isDiscarding,
					isActive: automationStatus.isActive,
					onPublish: handlePublishClick,
					onDiscard: handleDiscardClick,
					onToggleStatus: automationStatus.toggleStatus,
					onDelete: handleDeleteClick,
				})
			) : !isReadOnly ? (
				<EditorActionBar
					hasDraft={hasDraft}
					canPublish={publishAutomation.canPublish}
					canDiscard={discardDraft.canDiscard}
					isPublishing={publishAutomation.isPublishing}
					isDiscarding={discardDraft.isDiscarding}
					isActive={automationStatus.isActive}
					onPublish={handlePublishClick}
					onDiscard={handleDiscardClick}
					onToggleStatus={automationStatus.toggleStatus}
					onDelete={handleDeleteClick}
				/>
			) : null}
			{isPublishedPreview && (
				<div className={css(canvasEditorStyles.publishedPreviewBanner)}>
					<span>You are viewing the live published version.</span>
					<Button variant='primary' size='sm' onClick={() => handleToggleDraft(true)}>
						Switch to Draft
					</Button>
				</div>
			)}
			<RefreshTimingBanner
				staleScheduleRepairs={publishAutomation.staleScheduleRepairs}
				rootTemplateId={template?.id}
				canEdit={
					!isReadOnly &&
					!isPublishedPreview &&
					(template as (Api.IAutomationTemplate & { canEdit?: boolean }) | undefined)?.canEdit !== false
				}
				impersonationContext={impersonationContext}
				// Hard-reload on success rather than threading invalidation through React Query's
				// structural sharing, useAutomationFlow's cache, and convertTemplateToFlow's
				// per-call template map. Too many derivation layers to keep coherent reliably;
				// a reload guarantees the canvas re-derives against fresh server state.
				// useUnsavedChanges already binds beforeunload, so any in-flight local edits
				// trigger the standard browser prompt.
				onRefreshComplete={result => {
					if (result.failedStepIds.length === 0) {
						window.location.reload();
					}
				}}
			/>
			<div className={css(canvasEditorStyles.canvasContainer)}>
				<AutomationFlowErrorBoundary
					onReset={() => refetch()}
					onError={() => {
						errorMessages.push({
							title: 'An error occurred',
							messages: ["We couldn't display this automation. Please try again."],
						});
					}}
				>
					<ReactFlowProvider>
						<AutomationFlowCanvas
							mode={canvasMode}
							initialNodes={nodes}
							initialEdges={edges}
							onNodesChange={handleNodesChange}
							onEdgesChange={handleEdgesChange}
							onSelectionChange={handleSelectionChange}
							activeContacts={activeContacts}
							saveStatus={autoSave.saveStatus}
							lastSavedAt={autoSave.lastSavedAt}
							saveErrorMessage={autoSave.errorMessage}
							validationResult={publishAutomation.validationResult}
							hasValidationErrors={publishAutomation.hasErrors}
							hasValidationWarnings={publishAutomation.hasWarnings}
							selectedSwitchNodeId={selectedSwitchNodeId}
							templateId={templateId}
							automationTemplate={template}
							impersonationContext={impersonationContext}
							onAddStepToChain={mutations.handleAddStepToChain}
							onDeleteSteps={mutations.handleDeleteSteps}
							onDeleteBranchCases={mutations.handleDeleteBranchCases}
							onDuplicateStep={mutations.handleDuplicateStep}
							onPasteSteps={mutations.handlePasteSteps}
							onAddSegment={mutations.handleAddSegment}
							onReorderSteps={mutations.handleReorderSteps}
							onReorderSegments={mutations.handleReorderSegments}
							onMoveStepsCrossSegment={mutations.handleMoveStepsCrossSegment}
							onUpdateCaseCriteria={mutations.handleUpdateCaseCriteria}
							onUpdateCaseName={mutations.handleUpdateCaseName}
							invalidStepIds={publishAutomation.invalidStepIds}
							highlightedValidationIssue={highlightedValidationIssue}
							excludedStepTypes={effectiveExcludedStepTypes}
							syncVersion={canvasSyncVersion}
						/>
					</ReactFlowProvider>
				</AutomationFlowErrorBoundary>
			</div>
		</div>
	);

	const drawerPanels = (
		<>
			{canvasModeConfig.showNodePalette && (
				<StickyAppDrawerContent value='steps'>
					<div className={css(canvasEditorStyles.nodePaletteContainer)}>
						<NodePalette excludedStepTypes={effectiveExcludedStepTypes} />
					</div>
				</StickyAppDrawerContent>
			)}
			{showTestingContacts && (
				<StickyAppDrawerContent value='testing-contacts'>
					<TestingContactsDrawerContent
						templateId={templateId || ''}
						selectedContacts={testContacts}
						onContactsChange={handleTestContactsChange}
						impersonationContext={impersonationContext}
						useDraft={useDraft}
						segmentFilterMode={segmentFilterMode}
					/>
				</StickyAppDrawerContent>
			)}
			<StickyAppDrawerContent value='ai-assistant'>
				<DefaultUniversalAssistantChat />
			</StickyAppDrawerContent>
		</>
	);

	const drawerToolbarButtons = (
		<>
			{canvasModeConfig.showNodePalette && (
				<AppDrawerToolbarButton value='steps' tooltip='Step Palette'>
					<svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
						<rect x='3' y='3' width='6' height='6' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
						<rect x='11' y='3' width='6' height='6' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
						<rect x='3' y='11' width='6' height='6' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
						<rect x='11' y='11' width='6' height='6' rx='1' stroke='currentColor' strokeWidth='1.5' fill='none' />
					</svg>
				</AppDrawerToolbarButton>
			)}
			{showTestingContacts && (
				<AppDrawerToolbarButton value='testing-contacts' tooltip='Test Contacts'>
					<svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
						<circle cx='10' cy='6' r='3' stroke='currentColor' strokeWidth='1.5' fill='none' />
						<path
							d='M4 18c0-3.3 2.7-6 6-6s6 2.7 6 6'
							stroke='currentColor'
							strokeWidth='1.5'
							strokeLinecap='round'
							fill='none'
						/>
					</svg>
				</AppDrawerToolbarButton>
			)}
			<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
		</>
	);

	return (
		<AutomationCanvasEditorStepsProvider templateId={templateId} impersonationContext={impersonationContext}>
			<>
				{children({ contentArea, drawerPanels, drawerToolbarButtons })}
				<PublishDialog
					isOpen={isPublishDialogOpen}
					onClose={handleClosePublishDialog}
					onConfirm={handlePublishConfirm}
					isPublishing={publishAutomation.isPublishing}
					validationResult={publishAutomation.validationResult}
					automationName={template?.name || 'Automation'}
					onValidationIssueSelect={handleValidationIssueSelect}
				/>
				<DiscardDialog
					isOpen={isDiscardDialogOpen}
					onClose={() => setIsDiscardDialogOpen(false)}
					onConfirm={handleDiscardConfirm}
					isDiscarding={discardDraft.isDiscarding}
				/>
				<DeleteAutomationDialog
					isOpen={isDeleteDialogOpen}
					onClose={() => setIsDeleteDialogOpen(false)}
					onConfirm={handleDeleteConfirm}
					isDeleting={mutations.isDeleting}
					automationName={template?.name || 'Automation'}
				/>
			</>
		</AutomationCanvasEditorStepsProvider>
	);
}
